/**
 * Task Templates for quick task creation
 * One-click bundles of related tasks
 */

import { TASK_PRIORITY } from '@/services/finTasksService';
import { CONTEXT_TYPE } from '@/utils/contextLinking';

// Task categories (as strings, not constants)
const TASK_CATEGORY = {
  FINANCIAL: 'financial',
  SHOPPING: 'shopping',
  PERSONAL: 'personal',
  HOUSEHOLD: 'household'
};

// Priority mapping
const PRIORITY = {
  LOW: TASK_PRIORITY.LOW,
  MEDIUM: TASK_PRIORITY.MEDIUM,
  HIGH: TASK_PRIORITY.HIGH
};

export const TASK_TEMPLATES = {
  WEEKLY_MONEY_RESET: {
    id: 'weekly-money-reset',
    name: 'Weekly Money Reset',
    description: 'Review your spending and financial health',
    icon: '💰',
    color: 'emerald',
    tasks: [
      {
        title: 'Review grocery spending',
        description: 'Check how much was spent on groceries this week vs budget',
        category: TASK_CATEGORY.FINANCIAL,
        priority: PRIORITY.MEDIUM,
        isFinancialTask: true,
        contextType: CONTEXT_TYPE.CATEGORY,
        daysFromNow: 0 // Due today
      },
      {
        title: 'Check pending refunds',
        description: 'Review any pending refunds or cashback payments',
        category: TASK_CATEGORY.FINANCIAL,
        priority: PRIORITY.LOW,
        isFinancialTask: true,
        daysFromNow: 0
      },
      {
        title: 'Log extra income / side hustle',
        description: 'Record any additional income received this week',
        category: TASK_CATEGORY.FINANCIAL,
        priority: PRIORITY.LOW,
        isFinancialTask: true,
        daysFromNow: 0
      },
      {
        title: 'Update savings tracker',
        description: 'Check savings account balance and update your tracker',
        category: TASK_CATEGORY.FINANCIAL,
        priority: PRIORITY.LOW,
        isFinancialTask: true,
        daysFromNow: 1 // Due tomorrow
      }
    ]
  },

  MONTH_END_REVIEW: {
    id: 'month-end-review',
    name: 'Month-End Review',
    description: 'Close out the month and plan ahead',
    icon: '📊',
    color: 'blue',
    tasks: [
      {
        title: 'Review total spend per category',
        description: 'Compare actual spending vs budget for each category',
        category: TASK_CATEGORY.FINANCIAL,
        priority: PRIORITY.HIGH,
        isFinancialTask: true,
        contextType: CONTEXT_TYPE.BUDGET,
        daysFromNow: 0
      },
      {
        title: 'Adjust next month\'s budget',
        description: 'Set budget amounts for next month based on this month\'s performance',
        category: TASK_CATEGORY.FINANCIAL,
        priority: PRIORITY.HIGH,
        isFinancialTask: true,
        contextType: CONTEXT_TYPE.BUDGET,
        daysFromNow: 1
      },
      {
        title: 'Review subscription charges',
        description: 'Check all subscription payments and cancel unused ones',
        category: TASK_CATEGORY.FINANCIAL,
        priority: PRIORITY.MEDIUM,
        isFinancialTask: true,
        daysFromNow: 2
      },
      {
        title: 'Calculate net savings for the month',
        description: 'Total income minus expenses to track monthly savings',
        category: TASK_CATEGORY.FINANCIAL,
        priority: PRIORITY.MEDIUM,
        isFinancialTask: true,
        daysFromNow: 2
      },
      {
        title: 'Plan major purchases for next month',
        description: 'List any big expenses coming up and budget for them',
        category: TASK_CATEGORY.FINANCIAL,
        priority: PRIORITY.LOW,
        isFinancialTask: true,
        daysFromNow: 3
      }
    ]
  },

  BILL_HEALTH_CHECK: {
    id: 'bill-health-check',
    name: 'Bill Health Check',
    description: 'Review and optimize your recurring bills',
    icon: '🔍',
    color: 'purple',
    tasks: [
      {
        title: 'Compare broadband deal',
        description: 'Check if current broadband plan is still competitive',
        category: TASK_CATEGORY.FINANCIAL,
        priority: PRIORITY.MEDIUM,
        isFinancialTask: true,
        daysFromNow: 0
      },
      {
        title: 'Check electricity direct debit',
        description: 'Review electricity usage and payment amount',
        category: TASK_CATEGORY.FINANCIAL,
        priority: PRIORITY.MEDIUM,
        isFinancialTask: true,
        daysFromNow: 1
      },
      {
        title: 'Review mobile phone contract',
        description: 'Check if better deals are available or contract can be optimized',
        category: TASK_CATEGORY.FINANCIAL,
        priority: PRIORITY.LOW,
        isFinancialTask: true,
        daysFromNow: 2
      },
      {
        title: 'Review insurance policies',
        description: 'Compare insurance rates and coverage options',
        category: TASK_CATEGORY.FINANCIAL,
        priority: PRIORITY.LOW,
        isFinancialTask: true,
        daysFromNow: 7
      }
    ]
  },

  SHOPPING_PREP: {
    id: 'shopping-prep',
    name: 'Shopping Prep',
    description: 'Organize your weekly shopping',
    icon: '🛒',
    color: 'amber',
    tasks: [
      {
        title: 'Check pantry and fridge',
        description: 'Take inventory of what you already have',
        category: TASK_CATEGORY.SHOPPING,
        priority: PRIORITY.MEDIUM,
        contextType: CONTEXT_TYPE.LIST,
        daysFromNow: 0
      },
      {
        title: 'Plan meals for the week',
        description: 'Decide on breakfast, lunch, and dinner options',
        category: TASK_CATEGORY.SHOPPING,
        priority: PRIORITY.MEDIUM,
        daysFromNow: 0
      },
      {
        title: 'Create shopping list',
        description: 'List all items needed based on meal plan',
        category: TASK_CATEGORY.SHOPPING,
        priority: PRIORITY.HIGH,
        contextType: CONTEXT_TYPE.LIST,
        daysFromNow: 0
      },
      {
        title: 'Check for deals and coupons',
        description: 'Look for discounts on items you need',
        category: TASK_CATEGORY.SHOPPING,
        priority: PRIORITY.LOW,
        daysFromNow: 1
      }
    ]
  },

  DEBT_MANAGEMENT: {
    id: 'debt-management',
    name: 'Debt Management',
    description: 'Track and reduce your debts',
    icon: '📉',
    color: 'red',
    tasks: [
      {
        title: 'List all current debts',
        description: 'Create a comprehensive list with balances and interest rates',
        category: TASK_CATEGORY.FINANCIAL,
        priority: PRIORITY.HIGH,
        isFinancialTask: true,
        daysFromNow: 0
      },
      {
        title: 'Calculate minimum payments',
        description: 'Determine minimum monthly payment for each debt',
        category: TASK_CATEGORY.FINANCIAL,
        priority: PRIORITY.HIGH,
        isFinancialTask: true,
        daysFromNow: 0
      },
      {
        title: 'Create debt payoff plan',
        description: 'Prioritize debts (avalanche or snowball method)',
        category: TASK_CATEGORY.FINANCIAL,
        priority: PRIORITY.HIGH,
        isFinancialTask: true,
        daysFromNow: 1
      },
      {
        title: 'Set up automatic payments',
        description: 'Automate at least minimum payments to avoid late fees',
        category: TASK_CATEGORY.FINANCIAL,
        priority: PRIORITY.MEDIUM,
        isFinancialTask: true,
        daysFromNow: 2
      }
    ]
  },

  SAVINGS_BOOST: {
    id: 'savings-boost',
    name: 'Savings Boost',
    description: 'Find ways to increase your savings',
    icon: '🎯',
    color: 'green',
    tasks: [
      {
        title: 'Review last month\'s discretionary spending',
        description: 'Identify areas where you can cut back',
        category: TASK_CATEGORY.FINANCIAL,
        priority: PRIORITY.MEDIUM,
        isFinancialTask: true,
        contextType: CONTEXT_TYPE.CATEGORY,
        daysFromNow: 0
      },
      {
        title: 'Set savings goal for next month',
        description: 'Decide on a specific amount to save',
        category: TASK_CATEGORY.FINANCIAL,
        priority: PRIORITY.HIGH,
        isFinancialTask: true,
        contextType: CONTEXT_TYPE.GOAL,
        daysFromNow: 0
      },
      {
        title: 'Transfer to savings account',
        description: 'Move money into savings before you can spend it',
        category: TASK_CATEGORY.FINANCIAL,
        priority: PRIORITY.HIGH,
        isFinancialTask: true,
        daysFromNow: 1
      },
      {
        title: 'Research high-interest savings accounts',
        description: 'Find better rates for your emergency fund',
        category: TASK_CATEGORY.FINANCIAL,
        priority: PRIORITY.LOW,
        isFinancialTask: true,
        daysFromNow: 7
      }
    ]
  }
};

/**
 * Get all template categories
 */
export function getTemplateCategories() {
  return Object.values(TASK_TEMPLATES);
}

/**
 * Get template by ID
 */
export function getTemplateById(id) {
  return Object.values(TASK_TEMPLATES).find(template => template.id === id);
}

/**
 * Generate tasks from template
 * @param {Object} template - Template object
 * @param {string} listId - Target list ID
 * @returns {Array} Array of task objects ready to be created
 */
export function generateTasksFromTemplate(template, listId) {
  const today = new Date();
  
  return template.tasks.map(taskTemplate => {
    const task = {
      title: taskTemplate.title,
      description: taskTemplate.description || '',
      category: taskTemplate.category,
      priority: taskTemplate.priority,
      isFinancialTask: taskTemplate.isFinancialTask || false,
      listId: listId
    };

    // Add due date if specified
    if (taskTemplate.daysFromNow !== undefined) {
      const dueDate = new Date(today);
      dueDate.setDate(dueDate.getDate() + taskTemplate.daysFromNow);
      task.dueDate = dueDate.toISOString();
    }

    // Add context type if specified (but not ID/label - user can link later)
    if (taskTemplate.contextType) {
      task.contextType = taskTemplate.contextType;
      // Leave contextId and contextLabel empty - user will link manually
    }

    return task;
  });
}

/**
 * Get color classes for template badge
 */
export function getTemplateColorClasses(color) {
  const colorMap = {
    emerald: {
      bg: 'bg-emerald-50',
      text: 'text-emerald-700',
      border: 'border-emerald-200',
      hover: 'hover:bg-emerald-100'
    },
    blue: {
      bg: 'bg-blue-50',
      text: 'text-blue-700',
      border: 'border-blue-200',
      hover: 'hover:bg-blue-100'
    },
    purple: {
      bg: 'bg-purple-50',
      text: 'text-purple-700',
      border: 'border-purple-200',
      hover: 'hover:bg-purple-100'
    },
    amber: {
      bg: 'bg-amber-50',
      text: 'text-amber-700',
      border: 'border-amber-200',
      hover: 'hover:bg-amber-100'
    },
    red: {
      bg: 'bg-red-50',
      text: 'text-red-700',
      border: 'border-red-200',
      hover: 'hover:bg-red-100'
    },
    green: {
      bg: 'bg-green-50',
      text: 'text-green-700',
      border: 'border-green-200',
      hover: 'hover:bg-green-100'
    }
  };

  return colorMap[color] || colorMap.blue;
}
